import type { ReactNode } from 'react'
import { clientContacts, jobContactDuties, jobContactTags } from '@fieldops/shared'
import { cn } from '../../lib/cn.js'
import { Icon, type IconName } from '../Icon.js'
import { toneStyles, type Tone } from '../ui.js'
import type { Job } from './types.js'

/**
 * The furniture every section of the job card is built from.
 *
 * One heading treatment, one rule, one fact pair. They live here rather than
 * being redeclared per pane so that eight sections cannot drift into eight
 * slightly different ideas of what a section heading looks like.
 *
 * The card reads as one white page with a tinted side column, not a stack of
 * boxes: `Panel` is a section set off from the next by space, `Label` is its
 * sentence-case title in the brand's navy Archivo, and `Section` is what goes
 * inside one. A box is kept for things that are objects in their own right — a
 * booking, a service, a person, the site and client — and not for the sections
 * that hold them. ADR 025.
 */

export function Section({
  title,
  hint,
  action,
  children,
}: {
  title: string
  hint?: string
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="flex flex-col gap-2.5">
      <div className="flex items-center gap-2.5">
        <Label>{title}</Label>
        {hint ? <span className="text-xs text-ink-500">{hint}</span> : null}
        {action ? <div className="ml-auto">{action}</div> : null}
      </div>
      {children}
    </section>
  )
}

export function Label({ children }: { children: ReactNode }) {
  return (
    <span className="font-display text-[15px] leading-snug font-bold text-brand-800">
      {children}
    </span>
  )
}

/**
 * One section of the page. The column holding panels stacks them with no gap;
 * the padding is the panel's.
 */
export function Panel({
  className,
  padded = true,
  children,
}: {
  className?: string
  /** Off for a panel that draws its own rows to the edge, like a line table. */
  padded?: boolean
  children: ReactNode
}) {
  return (
    <div
      className={cn('flex flex-col', padded ? 'gap-2.5 py-2.5' : 'overflow-hidden', className)}
    >
      {children}
    </div>
  )
}

/** The heading row inside a `Panel`: eyebrow, optional count, optional action. */
export function PanelHeading({
  title,
  hint,
  action,
}: {
  title: string
  hint?: string
  action?: ReactNode
}) {
  return (
    <div className="flex items-center gap-2.5">
      <Label>{title}</Label>
      {hint ? <span className="text-xs text-ink-500">{hint}</span> : null}
      {action ? <div className="ml-auto flex items-center gap-2">{action}</div> : null}
    </div>
  )
}

/**
 * The client and the site, drawn the same way.
 *
 * A light tile with the record's glyph, what kind of record it is, its name,
 * and a way through to the whole of it. Both appear at the top of Work as a summary and again as the
 * first panel of their own tab, so they are one component rather than four
 * near-identical headers.
 */
export function RecordHeader({
  icon,
  kind,
  name,
  detail,
  action,
}: {
  icon: IconName
  kind: string
  name: string
  /** A muted line under the name: the address, usually. */
  detail?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-800">
        <Icon name={icon} className="size-4" />
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="text-[11px] text-ink-500">{kind}</span>
        <span className="truncate text-[14px] font-bold text-ink-900">{name}</span>
        {detail ? <span className="truncate text-[12.5px] text-ink-600">{detail}</span> : null}
      </span>
      {action ? <div className="ml-auto shrink-0">{action}</div> : null}
    </div>
  )
}

/**
 * A fact about a record, as a pill.
 *
 * Neutral by default. A tone is for the one that changes how somebody
 * approaches the property — a dog, a credit hold — and never for decoration,
 * because four coloured pills in a row means none of them is a warning.
 */
export function Tag({ tone, children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] whitespace-nowrap',
        tone
          ? cn('font-semibold ring-1 ring-inset', toneStyles[tone])
          : 'bg-brand-50 font-semibold text-brand-800',
      )}
    >
      {children}
    </span>
  )
}

export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-xs font-medium tracking-wide text-ink-500 uppercase">{label}</dt>
      <dd className="truncate text-[13px] text-ink-900">{children}</dd>
    </div>
  )
}

/**
 * A label and a value in a side rail's two-column list, inside a
 * `grid-cols-[auto_1fr]` `<dl>`. The wrapper is `contents` so the pair stays
 * two cells of that grid.
 */
export function RailFact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="contents">
      <dt className="text-ink-500">{label}</dt>
      <dd className="min-w-0 text-ink-900">{children}</dd>
    </div>
  )
}

export function Rule() {
  return <div className="h-px bg-ink-100" />
}

/**
 * A section that exists in the navigation but has nothing behind it yet.
 *
 * These are here because the card's shape is the eight things a job has, and
 * leaving two of them out would teach people the job card is where six of them
 * live. What is *not* here is a mocked-up feed: the panel names the endpoint
 * that is missing, so nobody spends ten minutes wondering why their messages
 * are not loading, and nobody demonstrates a feature that does not exist.
 */
export function NotBuiltYet({ title, what, why }: { title: string; what: string; why: string }) {
  return (
    <div className="rounded-lg bg-ink-50 px-4 py-6 ring-1 ring-ink-200">
      <p className="text-sm font-semibold text-ink-900">{title}</p>
      <p className="mt-1 max-w-prose text-[13px] leading-relaxed text-ink-600">{what}</p>
      <p className="mt-2 max-w-prose text-xs leading-relaxed text-ink-500">{why}</p>
    </div>
  )
}

export interface Person {
  id: string
  name: string
  tag: string
  sub: string
  phone: string | null
  email: string | null
  primary: boolean
}

/**
 * Who the header's Call and Email buttons ring.
 *
 * The job contact (`jobContactDuties`) — the person dealing with us about the
 * work — and, on a job nobody has been named on, the client's own primary
 * contact. A site's people are not that fallback. Decisions 84 and 85.
 */
export function primaryPerson(job: Job): Person | undefined {
  const duties = jobContactDuties(job.contacts)
  const link = duties.job ?? duties.billing

  if (link) {
    return {
      ...personFrom(link.contact),
      id: link.id,
      tag: jobContactTags(link, duties.sole)[0] ?? '',
    }
  }

  const pool = clientContacts(job.client.contacts)
  const contact = pool.find((row) => row.isPrimary) ?? pool[0]
  return contact
    ? { ...personFrom(contact), tag: contact.isPrimary ? 'Primary' : 'Client' }
    : undefined
}

/** Everyone on the job with an address, for the compose window's To list. */
export function emailContacts(job: Job) {
  const people = job.contacts.flatMap((link) => {
    const email = link.contact.email
    if (!email) return []
    const name = [link.contact.firstName, link.contact.lastName].filter(Boolean).join(' ')
    return [{ name: name || email, email }]
  })
  if (job.client.email && !people.some((person) => person.email === job.client.email)) {
    people.push({ name: job.client.name, email: job.client.email })
  }
  return people
}

type ContactRecord = Job['client']['contacts'][number]

export function personFrom(contact: ContactRecord): Person {
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ')
  const phone = contact.mobile ?? contact.phone

  return {
    id: contact.id,
    name,
    tag: '',
    sub: [contact.position, phone].filter(Boolean).join(' · ') || 'No number on file',
    phone,
    email: contact.email,
    primary: contact.isPrimary,
  }
}

/**
 * The booking anything on the card acts on: the next one still to happen, or
 * the most recent if they are all behind us. Rescheduling the first of six
 * visits on a contract job because it happens to be first in the list is the
 * wrong one every time after the first.
 */
export function nextVisit(job: Job): Job['visits'][number] | undefined {
  const live = job.visits.filter(
    (visit) => visit.status !== 'CANCELLED' && visit.status !== 'NO_SHOW',
  )
  const now = Date.now()

  return live.find((visit) => visit.endAt.getTime() >= now) ?? live[live.length - 1]
}
